import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type DeviceLookupInfo } from '../api';
import { useAuth } from '../auth';
import { Button, Card, Field, Input, Page } from '../ui';

/**
 * Tela de confirmação do app de sincronização. O app abre o navegador aqui com
 * `?code=XXXX-XXXX`; o usuário logado confirma e o app recebe o token via polling.
 */
export function Connect() {
  const { me } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('code') ?? '').toUpperCase());
  const [device, setDevice] = useState<DeviceLookupInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<'approved' | 'denied' | null>(null);
  const [busy, setBusy] = useState(false);

  const lookup = (c: string) => {
    setErr(null);
    setDevice(null);
    if (!c.trim()) return;
    api
      .deviceLookup(c.trim())
      .then(({ device }) => setDevice(device))
      .catch((e) => setErr(e.status === 404 ? 'Código não encontrado ou já usado.' : 'Falha ao buscar.'));
  };

  useEffect(() => {
    const initial = (params.get('code') ?? '').toUpperCase();
    if (initial) lookup(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function approve() {
    setBusy(true);
    try {
      await api.deviceApprove(code.trim());
      setDone('approved');
    } catch {
      setErr('Não foi possível autorizar (código inválido ou expirado).');
    } finally {
      setBusy(false);
    }
  }

  async function deny() {
    setBusy(true);
    try {
      await api.deviceDeny(code.trim());
      setDone('denied');
    } catch {
      setErr('Não foi possível recusar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Conectar o app de sincronização" subtitle="Autorize o app de docs a acessar sua conta.">
      <div style={{ maxWidth: 520 }}>
        <Card>
          {done === 'approved' && (
            <p style={{ color: 'var(--color-ok)' }}>
              ✓ App autorizado como <strong>{me?.displayName ?? me?.email}</strong>. Pode voltar pro
              app — ele vai concluir a conexão sozinho.
            </p>
          )}
          {done === 'denied' && <p style={{ color: 'var(--color-muted)' }}>Pedido recusado.</p>}

          {!done && (
            <>
              <Field label="Código exibido no app">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onBlur={() => lookup(code)}
                  placeholder="WXYZ-1234"
                  autoFocus
                />
              </Field>

              {err && <div className="ui-error-banner">{err}</div>}

              {device && (
                <div style={{ margin: '12px 0' }}>
                  <p style={{ marginBottom: 4 }}>
                    O app <strong>“{device.label}”</strong> quer acessar sua conta.
                  </p>
                  <p style={{ color: 'var(--color-muted)', fontSize: 12 }}>
                    {device.expired
                      ? 'Este pedido expirou — gere um novo no app.'
                      : `Código ${device.userCode}. Autorize só se foi você que abriu o app.`}
                  </p>
                </div>
              )}

              <div className="actions" style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="primary"
                  onClick={approve}
                  disabled={busy || !device || device.expired || device.status !== 'pending'}
                >
                  {busy ? 'autorizando…' : 'Autorizar'}
                </Button>
                <Button variant="ghost" onClick={deny} disabled={busy || !device}>
                  Recusar
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Page>
  );
}
